import React, { useState, useEffect } from 'react'
import { SMBeamModel, SectionMaterialStrength, AnalysisResult, ReportGenerationRequest, ReportGenerationResponse, ReportListItem, VerificationRequest, VerificationResponse } from '../types'
import { UseMaterialStateResult } from '../hooks/useMaterialState'
import ReportSettingsDialog, { ReportSettings } from '../components/ReportSettingsDialog'
import VerificationDialog from '../components/VerificationDialog'
import { useTranslation } from '@structura/i18n'

interface ReportTabProps {
  model: SMBeamModel
  setModel: React.Dispatch<React.SetStateAction<SMBeamModel>>
  result?: AnalysisResult | null
  currentMaterial: 'steel' | 'concrete' | 'timber' | string
  sectionMaterialStrength?: SectionMaterialStrength | null
  materialState: UseMaterialStateResult
  getSessionHeaders: () => HeadersInit
}

const ReportTab: React.FC<ReportTabProps> = ({ model: _model, setModel: _setModel, result, currentMaterial: _currentMaterial, sectionMaterialStrength: _sectionMaterialStrength, materialState: _materialState, getSessionHeaders }) => {
  const { t, language } = useTranslation()
  const [showDialog, setShowDialog] = useState(false)
  const [generating, setGenerating] = useState(false)
  const [reports, setReports] = useState<ReportListItem[]>([])
  const [loadingList, setLoadingList] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showVerifyDialog, setShowVerifyDialog] = useState(false)
  const [verifyingReportId, setVerifyingReportId] = useState<string>('')
  const [verifying, setVerifying] = useState(false)

  useEffect(() => {
    loadReportList()
  }, [])

  const loadReportList = async () => {
    setLoadingList(true)
    setError(null)
    try {
      const response = await fetch('/api/CalculationReport/list', {
        headers: getSessionHeaders()
      })
      if (response.ok) {
        const data: ReportListItem[] = await response.json()
        setReports(data)
      } else {
        setError(t('beam.report.errLoadList'))
      }
    } catch (err) {
      setError(t('beam.report.errLoading', { message: (err as Error).message }))
    } finally {
      setLoadingList(false)
    }
  }

  const handleGenerateReport = async (settings: ReportSettings) => {
    if (!result || !result.success) {
      setError(t('beam.report.errResultRequired'))
      return
    }

    // Extract analysisId from result
    const analysisId = (result as any)?.analysisId ?? (result as any)?.AnalysisId

    if (!analysisId) {
      setError(t('beam.report.errNoAnalysisId'))
      return
    }

    setGenerating(true)
    setError(null)

    try {
      // SIMPLIFIED: Only send project metadata and AnalysisId
      // All geometry, materials, loads, etc. are loaded from AnalysisSessions.db
      const request: ReportGenerationRequest = {
        projectName: settings.projectName,
        projectNumber: settings.projectNumber,
        engineerName: settings.engineerName,
        companyName: settings.companyName,
        projectDescription: settings.projectDescription,
        projectPurpose: settings.projectPurpose,
        language: language,
        analysisId: analysisId,
        // Optional: Response data if available
        responseData: (result as any)?.ResponseData ?? (result as any)?.responseData
      }

      const response = await fetch('/api/CalculationReport/generate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getSessionHeaders()
        },
        body: JSON.stringify(request)
      })

      if (response.ok) {
        const data: ReportGenerationResponse = await response.json()
        
        // Download the PDF
        await downloadReport(data.reportId)

        // Reload list
        await loadReportList()
        
        setShowDialog(false)
      } else {
        const errorData = await response.json()
        setError(errorData.message || t('beam.report.errGenerate'))
      }
    } catch (err) {
      setError(t('beam.report.errGenerating', { message: (err as Error).message }))
    } finally {
      setGenerating(false)
    }
  }

  const downloadReport = async (reportId: string) => {
    try {
      const response = await fetch(`/api/CalculationReport/${reportId}/download`, {
        headers: getSessionHeaders()
      })
      
      if (response.ok) {
        const blob = await response.blob()
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `Calculation_Report_${new Date().toISOString().split('T')[0]}.pdf`
        document.body.appendChild(a)
        a.click()
        window.URL.revokeObjectURL(url)
        document.body.removeChild(a)
      } else {
        // Surface the real cause (HTTP status + backend message + id) instead of a generic failure.
        let backendMessage = ''
        try {
          const body = await response.json()
          backendMessage = body?.message ? ` – ${body.message}` : ''
        } catch {
          // response body was not JSON (e.g. empty 404/500 body)
        }
        setError(`${t('beam.report.errDownload')} (HTTP ${response.status}${backendMessage}, reportId=${reportId})`)
      }
    } catch (err) {
      setError(t('beam.report.errDownloading', { message: `${(err as Error).message} (reportId=${reportId})` }))
    }
  }

  const deleteReport = async (reportId: string) => {
    if (!confirm(t('beam.report.confirmDelete'))) {
      return
    }

    try {
      const response = await fetch(`/api/CalculationReport/${reportId}`, {
        method: 'DELETE',
        headers: getSessionHeaders()
      })

      if (response.ok) {
        await loadReportList()
      } else {
        // Surface the real cause (HTTP status + backend message + id).
        let backendMessage = ''
        try {
          const body = await response.json()
          backendMessage = body?.message ? ` – ${body.message}` : ''
        } catch {
          // response body was not JSON
        }
        setError(`${t('beam.report.errDelete')} (HTTP ${response.status}${backendMessage}, reportId=${reportId})`)
      }
    } catch (err) {
      setError(t('beam.report.errDeleting', { message: `${(err as Error).message} (reportId=${reportId})` }))
    }
  }

  const handleVerifyClick = (reportId: string) => {
    setVerifyingReportId(reportId)
    setShowVerifyDialog(true)
  }

  const handleVerify = async (reportId: string, request: VerificationRequest) => {
    setVerifying(true)
    setError(null)

    try {
      const response = await fetch(`/api/CalculationReport/${reportId}/verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getSessionHeaders()
        },
        body: JSON.stringify(request)
      })

      if (response.ok) {
        await response.json() as VerificationResponse
        await loadReportList()
        setShowVerifyDialog(false)
      } else {
        const errorData = await response.json()
        setError(errorData.message || t('beam.report.errVerify'))
      }
    } catch (err) {
      setError(t('beam.report.errVerifying', { message: (err as Error).message }))
    } finally {
      setVerifying(false)
    }
  }

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString)
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString()
  }

  return (
    <div className="container-fluid">
      <div className="row g-3">
        <div className="col-12">
          <div className="card">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h5 className="mb-0">{t('beam.report.title')}</h5>
              <button className="btn beam-btn-primary" onClick={() => setShowDialog(true)} disabled={!result || !result.success}>
                <i className="bi bi-file-earmark-pdf me-2"></i>
                {t('beam.report.generateNew')}
              </button>
            </div>
            <div className="card-body">
              {error && (
                <div className="alert alert-danger alert-dismissible fade show" role="alert">
                  {error}
                  <button type="button" className="btn-close" onClick={() => setError(null)}></button>
                </div>
              )}

              {!result || !result.success ? (
                <div className="beam-alert-info">
                  <i className="bi bi-info-circle me-2"></i>
                  {t('beam.report.completeAnalysisFirst')}
                </div>
              ) : (
                <div className="beam-alert-success">
                  <i className="bi bi-check-circle me-2"></i>
                  {t('beam.report.analysisCompleteCanGenerate')}
                </div>
              )}

              <h6 className="mt-4 mb-3">{t('beam.report.previousReports')}</h6>
              {loadingList ? (
                <div className="text-center py-4">
                  <div className="spinner-border" role="status">
                    <span className="visually-hidden">{t('beam.status.loading')}</span>
                  </div>
                </div>
              ) : reports.length === 0 ? (
                <p className="text-muted">{t('beam.report.noReports')}</p>
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover">
                    <thead>
                      <tr>
                        <th>{t('beam.report.thProjectName')}</th>
                        <th>{t('beam.report.thProjectId')}</th>
                        <th>{t('beam.report.thBeamType')}</th>
                        <th>{t('beam.report.thGenerated')}</th>
                        <th>{t('beam.report.thVerifiedBy')}</th>
                        <th>{t('beam.report.thActions')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reports.map((report) => (
                        <tr key={report.id}>
                          <td>{report.projectName}</td>
                          <td>{report.projectNumber}</td>
                          <td>
                            <div>{report.beamType}</div>
                            <div className="text-muted">{report.materialType}</div>
                          </td>
                          <td>{formatDate(report.generatedDate)}</td>
                          <td style={{ maxWidth: '250px' }}>
                            {report.verificationStatus === 0 ? (
                              <button
                                className="btn btn-sm btn-success"
                                onClick={() => handleVerifyClick(report.id)}
                                title={t('beam.report.verifyThis')}
                                style={{ backgroundColor: '#28a745', borderColor: '#28a745' }}
                              >
                                <i className="bi bi-shield-check me-1"></i>
                                {t('beam.report.verify')}
                              </button>
                            ) : (
                              <div style={{ lineHeight: '1.3' }}>
                                <div className="mb-1 d-flex align-items-center flex-wrap">
                                  <span className={`badge ${report.verificationStatus === 1 ? 'bg-success' : 'bg-danger'}`}>
                                    <i className="bi bi-patch-check-fill me-1"></i>
                                    {report.verificationStatus === 1 ? t('beam.report.statusVerified') : t('beam.report.statusNotApproved')}
                                  </span>
                                  {report.verificationDate && (
                                    <span className="ms-2 text-muted">
                                      {new Date(report.verificationDate).toLocaleDateString()}
                                    </span>
                                  )}
                                </div>
                                <div
                                  className="text-truncate"
                                  style={{ whiteSpace: 'nowrap' }}
                                  title={`${report.verifierCompanyName} - ${report.verifierPersonName}`}
                                >
                                  <strong>{report.verifierCompanyName}</strong> · {report.verifierPersonName}
                                </div>
                              </div>
                            )}
                          </td>
                          <td>
                            <div className="btn-group btn-group-sm">
                              <button className="btn btn-outline-primary" onClick={() => downloadReport(report.id)} title={t('beam.report.downloadPdf')}>
                                <i className="bi bi-download"></i>
                              </button>
                              <button className="btn btn-outline-danger" onClick={() => deleteReport(report.id)} title={t('beam.report.delete')}>
                                <i className="bi bi-trash"></i>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <ReportSettingsDialog show={showDialog} onClose={() => setShowDialog(false)} onGenerate={handleGenerateReport} generating={generating} getSessionHeaders={getSessionHeaders} />
      <VerificationDialog show={showVerifyDialog} reportId={verifyingReportId} onClose={() => setShowVerifyDialog(false)} onVerify={handleVerify} verifying={verifying} />
    </div>
  )
}

export default ReportTab
